import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProductionLayoutWrapper from "../../components/ProductionLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaPlus,
  FaChevronDown,
  FaChevronUp,
  FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const toDDMMYYYY = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const ProductionBOMListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;
  const navigate = useNavigate();

  // ✅ Safe localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 8), [branchIdLS]);
  const branch = useMemo(() => String(branchTypeLS || "").trim(), [branchTypeLS]);
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-branchtype": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [showFilters, setShowFilters] = useState(true);

  const defaultFormData = {
    fromDate: toDDMMYYYY(new Date()),
    toDate: toDDMMYYYY(new Date()),
    projectid: 0,
    productionid: 0,
  };

  const [formData, setFormData] = useState(defaultFormData);

  const [projects, setProjects] = useState([]);
  const [productions, setProductions] = useState([]);
  const [bomData, setBomData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // ✅ selection
  const [selectedIds, setSelectedIds] = useState([]);

  // ✅ Total qty for Results Summary panel
  const totalQty = useMemo(
    () => bomData.reduce((sum, r) => sum + (Number(r.productionQty) || 0), 0),
    [bomData]
  );

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const loadProjects = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/bomlisting/projects`, {
        params: { branchId, userId },
        headers: commonHeaders,
      });
      setProjects(res.data?.data || []);
    } catch (err) {
      console.error("Projects load error:", err);
      toast.error(err?.response?.data?.message || "Failed to load projects");
      setProjects([]);
    }
  };

  const loadProductions = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/bomlisting/productions`, {
        params: { branchId, userId },
        headers: commonHeaders,
      });
      setProductions(res.data?.data || []);
    } catch (err) {
      console.error("Productions load error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to load production names"
      );
      setProductions([]);
    }
  };

  useEffect(() => {
    loadProjects();
    loadProductions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, userId]);

  const handleSearch = async () => {
    if (!branchId) {
      toast.error("BranchId missing!");
      return;
    }

    if (!formData.fromDate || !formData.toDate) {
      toast.warn("Please select From Date and To Date");
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const res = await axios.get(`${BASE_URL}/api/bomlisting/search`, {
        params: {
          branchId,
          userId,
          fromDate: formData.fromDate,
          toDate: formData.toDate,
          projectid: Number(formData.projectid || 0),
          productionid: Number(formData.productionid || 0),
        },
        headers: commonHeaders,
      });

      const rows = res.data?.data || [];
      setBomData(rows);
      setSelectedIds([]);

      if (rows.length === 0) toast.info("No Records found");
    } catch (err) {
      console.error("Search error:", err);
      toast.error(err?.response?.data?.message || "Search failed");
      setBomData([]);
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Clear filters back to today's date range and reload
  const handleClearFilters = () => {
    setFormData(defaultFormData);
    setTimeout(() => handleSearch(), 0);
  };

  const handleAddNew = () => {
    navigate("/TarMixing/Tar_BOM_Entry");
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const allSelected =
    bomData.length > 0 && selectedIds.length === bomData.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(bomData.map((r) => Number(r.bomId)));
    }
  };

  // ✅ Update — same navigation logic, now triggered per-row via Edit icon
  const handleUpdateRow = (row) => {
    const id = Number(row.bomId);

    navigate("/TarMixing/Tar_BOM_Entry", {
      state: {
        editMode: true,
        bomId: id,
        rowData: row,
      },
    });
  };

  // ✅ Delete (bulk, via Remove button)
  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one record to delete.");
      return;
    }

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      const res = await axios.delete(
        `${BASE_URL}/api/bomlisting/deleteSelected`,
        {
          params: {
            branchid: branchId,
            loginuserid: userId,
          },
          data: { bomids: selectedIds },
          headers: commonHeaders,
        }
      );

      if (res.data?.success === false) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      toast.success("Deleted successfully!");
      setBomData((prev) =>
        prev.filter((r) => !selectedIds.includes(Number(r.bomId)))
      );
      setSelectedIds([]);
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  return (
    <ProductionLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PRODUCTION BOM LISTING</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Production BOM Listing
            </h2>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={formData.fromDate}
                    onChange={(e) => handleInputChange("fromDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={formData.toDate}
                    onChange={(e) => handleInputChange("toDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Project
                  </label>
                  <select
                    value={formData.projectid}
                    onChange={(e) => handleInputChange("projectid", Number(e.target.value))}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value={0}>All Projects</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Production Name
                  </label>
                  <select
                    value={formData.productionid}
                    onChange={(e) => handleInputChange("productionid", Number(e.target.value))}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value={0}>All Productions</option>
                    {productions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : bomData.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Entry Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Project</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Production Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Production Code</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Qty</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">UOM</th>

                      {/* EDIT - SECOND LAST COLUMN */}
                      <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>

                      {/* SELECT ALL - LAST COLUMN */}
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                        />
                      </th>
                    </tr>
                  </thead>

                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {bomData.map((row, idx) => {
                      const id = Number(row.bomId);
                      const checked = selectedIds.includes(id);

                      return (
                        <tr
                          key={id || idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark ? "bg-gray-800" : "bg-white"
                              : isDark ? "bg-gray-750" : "bg-gray-50"
                          } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {idx + 1}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row.entryDate || "-"}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row.projectName || "-"}
                          </td>
                          <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                            {row.productionName || "-"}
                          </td>
                          <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                            {row.productionCode || "-"}
                          </td>
                          <td className={`px-6 py-4 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                            {row.productionQty ?? 0}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row.tarUOM || "-"}
                          </td>

                          {/* EDIT */}
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleUpdateRow(row)}
                              className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                              title="Edit"
                            >
                              ✏️
                            </button>
                          </td>

                          {/* SELECT */}
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRow(id)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* ==================== REMOVE BUTTON ==================== */}
                <div className="p-4">
                  <button
                    onClick={handleDelete}
                    disabled={selectedIds.length === 0}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                      selectedIds.length === 0 ? "opacity-50 cursor-not-allowed" : ""
                    } bg-red-600 hover:bg-red-700 text-white`}
                  >
                    🗑
                    Remove
                  </button>
                </div>
              </>
            )}
          </div>

      
          <ToastContainer />
        </div>
      </div>
    </ProductionLayoutWrapper>
  );
};

export default ProductionBOMListing;